import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type ThemePreference = 'light' | 'dark'

type ThemeContextValue = {
  preference: ThemePreference
  scheme: ThemePreference
  setPreference: (preference: ThemePreference) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'whyfelt.theme'

function systemScheme(): ThemePreference {
  if (typeof matchMedia === 'undefined') return 'light'
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readStoredPreference(): ThemePreference {
  if (typeof localStorage === 'undefined') return systemScheme()
  const stored = localStorage.getItem(STORAGE_KEY)
  // First visit (or a legacy "system" value) follows the OS; after the user
  // picks light/dark it's explicit and sticks.
  return stored === 'light' || stored === 'dark' ? stored : systemScheme()
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(readStoredPreference)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', preference)
    document.documentElement.style.colorScheme = preference
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    localStorage.setItem(STORAGE_KEY, next)
  }, [])

  const value = useMemo(
    () => ({ preference, scheme: preference, setPreference }),
    [preference, setPreference],
  )

  return <ThemeContext value={value}>{children}</ThemeContext>
}
